import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SuperAdminAuthProvider } from './context/SuperAdminAuthContext';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import AdminLayout from './components/admin/AdminLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import SuperAdminProtectedRoute from './components/common/SuperAdminProtectedRoute';

// ✅ NEW — Landing Page
import LandingPage from './pages/LandingPage';

// ✅ NEW — 404 Page + Slug Validator
import NotFound404 from './pages/NotFound404';
import SlugValidator from './components/common/SlugValidator';

// Public pages
import Home from './pages/Home';
import Menu from './pages/Menu';
import Order from './pages/Order';
import Reservation from './pages/Reservation';
import { Gallery, About, Contact } from './pages/StaticPages';
import TrackOrder from './pages/TrackOrder';

// Restaurant admin pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMenu from './pages/admin/AdminMenu';
import AdminOrders from './pages/admin/AdminOrders';
import AdminReservations from './pages/admin/AdminReservations';
import AdminGallery from './pages/admin/AdminGallery';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSettings from './pages/admin/AdminSettings';
import AdminReviews from './pages/admin/AdminReviews';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminTables from './pages/admin/AdminTables';
import StaffDashboard from './pages/admin/StaffDashboard';
import AdminMessages from './pages/admin/AdminMessages';

// Super Admin pages
import SuperAdminLogin from './pages/super-admin/SuperAdminLogin';
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import SuperAdminRestaurants from './pages/super-admin/SuperAdminRestaurants';
import SuperAdminRestaurantDetail from './pages/super-admin/SuperAdminRestaurantDetail';
import SuperAdminSettings from './pages/super-admin/SuperAdminSettings';
import SuperAdminConfirmEmail from './pages/super-admin/SuperAdminConfirmEmail';
import SuperAdminLayout from './components/super-admin/SuperAdminLayout';

const toastStyle = {
  style: {
    background: '#ffffff',
    color: '#1c1512',
    border: '1px solid #e8e0d8',
    borderRadius: '12px',
    fontSize: '14px',
    boxShadow: '0 4px 20px rgba(28,21,18,0.10)',
  },
  success: { iconTheme: { primary: '#b85c38', secondary: '#ffffff' } },
};

// Public layout with Navbar + Footer
const PublicLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-white">
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

const App = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <SuperAdminAuthProvider>
          <AuthProvider>
            <CartProvider>
              <Toaster position="top-right" toastOptions={{ duration: 3000, ...toastStyle }} />
              <Routes>

                {/* ── ✅ Landing Page — "/" par ────────────────────────────── */}
                <Route path="/" element={<LandingPage />} />

                {/* ── Super Admin Routes ─────────────────────────────────────── */}
                <Route path="/super-admin/login" element={<SuperAdminLogin />} />
                <Route path="/super-admin/change-email/confirm" element={<SuperAdminConfirmEmail />} />
                <Route path="/super-admin" element={
                  <SuperAdminProtectedRoute><SuperAdminLayout /></SuperAdminProtectedRoute>
                }>
                  <Route index element={<SuperAdminDashboard />} />
                  <Route path="restaurants" element={<SuperAdminRestaurants />} />
                  <Route path="restaurants/:id" element={<SuperAdminRestaurantDetail />} />
                  <Route path="settings" element={<SuperAdminSettings />} />
                </Route>

                {/* ── Single Restaurant — Public Pages (:slug prefix) ────────── */}
                <Route path="/:slug" element={<SlugValidator><PublicLayout><Home /></PublicLayout></SlugValidator>} />
                <Route path="/:slug/menu" element={<SlugValidator><PublicLayout><Menu /></PublicLayout></SlugValidator>} />
                <Route path="/:slug/order" element={<SlugValidator><PublicLayout><Order /></PublicLayout></SlugValidator>} />
                <Route path="/:slug/reservation" element={<SlugValidator><PublicLayout><Reservation /></PublicLayout></SlugValidator>} />
                <Route path="/:slug/gallery" element={<SlugValidator><PublicLayout><Gallery /></PublicLayout></SlugValidator>} />
                <Route path="/:slug/about" element={<SlugValidator><PublicLayout><About /></PublicLayout></SlugValidator>} />
                <Route path="/:slug/contact" element={<SlugValidator><PublicLayout><Contact /></PublicLayout></SlugValidator>} />
                <Route path="/:slug/track-order" element={<SlugValidator><PublicLayout><TrackOrder /></PublicLayout></SlugValidator>} />

                {/* ── Single Restaurant — Admin Pages ────────────────────────── */}
                <Route path="/:slug/admin/login" element={<AdminLogin />} />
                <Route path="/:slug/staff" element={
                  <ProtectedRoute requiredRole="staff"><StaffDashboard /></ProtectedRoute>
                } />
                <Route path="/:slug/admin" element={
                  <ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>
                }>
                  <Route index element={<AdminDashboard />} />
                  <Route path="menu" element={<AdminMenu />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="reservations" element={<AdminReservations />} />
                  <Route path="gallery" element={<AdminGallery />} />
                  <Route path="tables" element={<AdminTables />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="reviews" element={<AdminReviews />} />
                  <Route path="coupons" element={<AdminCoupons />} />
                  <Route path="messages" element={<AdminMessages />} />
                </Route>

                {/* Fallback — legacy /admin/login support */}
                <Route path="/admin/login" element={<AdminLogin />} />

                {/* 404 fallback */}
                <Route path="*" element={<NotFound404 />} />

              </Routes>
            </CartProvider>
          </AuthProvider>
        </SuperAdminAuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
